import type { ClinicsFilters } from "../clinics.types";
import type { Clinic } from "../clinics.types";

/**
 * Count the number of active filters
 */
export function countActiveFilters(filters: ClinicsFilters): number {
  let count = 0;

  // Count search if it has a value
  if (filters.search && filters.search.trim().length > 0) {
    count++;
  }

  // Count array filters
  if (filters.services && filters.services.length > 0) count++;
  if (filters.languages && filters.languages.length > 0) count++;
  if (filters.conditions && filters.conditions.length > 0) count++;
  if (filters.treatments && filters.treatments.length > 0) count++;
  if (filters.financiers && filters.financiers.length > 0) count++;
  if (filters.consultationTypes && filters.consultationTypes.length > 0) {
    count++;
  }
  if (filters.patientGroups && filters.patientGroups.length > 0) count++;
  if (filters.accessibilities && filters.accessibilities.length > 0) count++;
  if (filters.employeeBenefits && filters.employeeBenefits.length > 0) {
    count++;
  }

  // Count single value filters
  if (filters.city) count++;
  if (filters.country) count++;
  if (filters.userLocation) count++; // Nearby filter
  if (filters.clinicType) count++;
  if (filters.isFirstConsultationFree !== undefined) count++;
  if (filters.hasReportedAnnualIvfData !== undefined) count++;
  if (filters.insuranceAccepted !== undefined) count++;

  return count;
}

/**
 * Apply client-side filters to clinics
 * These filters are applied after fetching from CMS because they require
 * client-side logic (e.g., array intersection checks)
 */
export function applyClientSideFilters(
  clinics: Clinic[],
  filters: ClinicsFilters,
): Clinic[] {
  let filtered = clinics;

  if (filters.treatments && filters.treatments.length > 0) {
    filtered = filtered.filter((clinic) => {
      const clinicTreatments = new Set(clinic.treatments);
      return filters.treatments!.every((treatment) =>
        clinicTreatments.has(treatment),
      );
    });
  }

  if (filters.services && filters.services.length > 0) {
    filtered = filtered.filter((clinic) => {
      const clinicServices = new Set(clinic.services);
      return filters.services!.every((service) => clinicServices.has(service));
    });
  }

  if (filters.languages && filters.languages.length > 0) {
    filtered = filtered.filter((clinic) => {
      const clinicLanguages = new Set(clinic.languages);
      return filters.languages!.every((language) =>
        clinicLanguages.has(language),
      );
    });
  }

  if (filters.conditions && filters.conditions.length > 0) {
    filtered = filtered.filter((clinic) => {
      const clinicConditions = new Set(clinic.conditions);
      return filters.conditions!.every((condition) =>
        clinicConditions.has(condition),
      );
    });
  }

  if (filters.patientGroups && filters.patientGroups.length > 0) {
    filtered = filtered.filter((clinic) => {
      const clinicGroups = new Set(clinic.patientGroups);
      return filters.patientGroups!.every((group) => clinicGroups.has(group));
    });
  }

  if (filters.consultationTypes && filters.consultationTypes.length > 0) {
    filtered = filtered.filter((clinic) => {
      const clinicTypes = new Set(clinic.consultationTypes);
      return filters.consultationTypes!.every((type) => clinicTypes.has(type));
    });
  }

  if (filters.accessibilities && filters.accessibilities.length > 0) {
    filtered = filtered.filter((clinic) => {
      const clinicAccessibilities = new Set(clinic.accessibilities);
      return filters.accessibilities!.every((accessibility) =>
        clinicAccessibilities.has(accessibility),
      );
    });
  }

  if (filters.hasReportedAnnualIvfData) {
    filtered = filtered.filter((clinic) => clinic.hasReportedAnnualIvfData);
  }

  return filtered;
}
